import { ConnectionState } from "../../types";
import type { LiveServerMessage } from "../../types";

import type { WsClientMessage, SetupPayload } from "../wsTypes";
import { normalizeSnakeToCamel } from "../wsNormalize";

export type WebSocketHandshakeStage =
  | "before_open"
  | "before_setup_ack"
  | "after_setup_ack";

export interface WebSocketFailureDetails {
  source: "websocket_error" | "websocket_close" | "setup_ack" | "server_error";
  handshakeStage: WebSocketHandshakeStage;
  socketOpened: boolean;
  setupAckReceived: boolean;
  readyState: number;
  elapsedMs: number;
  closeCode?: number;
  closeReason?: string;
  wasClean?: boolean;
  eventType?: string;
}

export class WebSocketClientError extends Error {
  public diagnostics?: WebSocketFailureDetails;

  constructor(message: string, diagnostics?: WebSocketFailureDetails) {
    super(message);
    this.name = "WebSocketClientError";
    this.diagnostics = diagnostics;
    Object.setPrototypeOf(this, WebSocketClientError.prototype);
  }
}

function getHandshakeStage(
  socketOpened: boolean,
  setupAckReceived: boolean,
): WebSocketHandshakeStage {
  if (!socketOpened) {
    return "before_open";
  }

  if (!setupAckReceived) {
    return "before_setup_ack";
  }

  return "after_setup_ack";
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private intentionalClose = false;

  // Callbacks
  public onStateChange: (state: ConnectionState) => void = () => {};
  public onMessage: (message: LiveServerMessage) => void = () => {};
  public onError: (
    error: string,
    details?: WebSocketFailureDetails,
  ) => void = () => {};
  public onSetupComplete: () => void = () => {};

  constructor() {}

  public async connect(url: string, setupPayload: SetupPayload): Promise<void> {
    this.updateState(ConnectionState.CONNECTING);
    this.intentionalClose = false;

    try {
      // WebSocket Creation
      const ws = new WebSocket(url);
      this.ws = ws;
      let socketOpened = false;
      let setupAckReceived = false;
      const connectStartedAt = Date.now();

      const buildDiagnostics = (
        overrides: Partial<WebSocketFailureDetails> &
          Pick<WebSocketFailureDetails, "source">,
      ): WebSocketFailureDetails => ({
        source: overrides.source,
        handshakeStage: getHandshakeStage(socketOpened, setupAckReceived),
        socketOpened,
        setupAckReceived,
        readyState: ws.readyState,
        elapsedMs: Date.now() - connectStartedAt,
        closeCode: overrides.closeCode,
        closeReason: overrides.closeReason,
        wasClean: overrides.wasClean,
        eventType: overrides.eventType,
      });

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => {
          socketOpened = true;
          // Send Setup Message
          this.send({ type: "setup", setup: setupPayload });
        };

        ws.onmessage = async (event) => {
          let data;
          try {
            let text = event.data;
            if (event.data instanceof Blob) {
              text = await event.data.text();
            }
            data = JSON.parse(text);
          } catch {
            return;
          }

          // Handle Handshake Responses
          if (data.type === "setup_ack") {
            setupAckReceived = true;
            if (data.ok) {
              this.handleOnOpen();
              resolve();
            } else {
              reject(
                new WebSocketClientError(
                  data.error?.message || "Setup failed",
                  buildDiagnostics({ source: "setup_ack" }),
                ),
              );
              this.ws?.close();
            }
            return;
          }

          if (data.setupComplete || data.setup_complete) {
            setupAckReceived = true;
            if (this.connectionState !== ConnectionState.CONNECTED) {
              this.handleOnOpen();
              resolve();
            }
            return;
          }

          if (data.type === "error" || data.error) {
            const errorPayload = data.error || data;
            const msg =
              typeof errorPayload === "string"
                ? errorPayload
                : errorPayload.message || JSON.stringify(errorPayload);
            const diagnostics = buildDiagnostics({ source: "server_error" });

            if (this.connectionState === ConnectionState.CONNECTING) {
              reject(new WebSocketClientError(msg, diagnostics));
              this.ws?.close();
              return;
            }

            this.handleServerError(msg, diagnostics);
            return;
          }

          // Handle Normal Messages
          const isContent =
            data.type === "server_message" ||
            data.serverContent ||
            data.server_content;

          if (isContent) {
            const normalized = normalizeSnakeToCamel(data);
            this.onMessage(normalized as LiveServerMessage);
          }
        };

        ws.onerror = (event) => {
          const diagnostics = buildDiagnostics({
            source: "websocket_error",
            eventType: event.type,
          });

          if (this.connectionState === ConnectionState.CONNECTING) {
            reject(
              new WebSocketClientError(
                "WebSocket connection error",
                diagnostics,
              ),
            );
            return;
          }

          this.handleOnError(diagnostics);
        };

        ws.onclose = (event) => {
          const diagnostics = buildDiagnostics({
            source: "websocket_close",
            closeCode: event.code,
            closeReason: event.reason,
            wasClean: event.wasClean,
          });

          if (this.connectionState === ConnectionState.CONNECTING) {
            reject(
              new WebSocketClientError(
                `WebSocket closed before setup: ${event.code}`,
                diagnostics,
              ),
            );
          }

          this.handleOnClose(event, diagnostics);
        };
      });
    } catch (error: unknown) {
      const diagnostics =
        error instanceof WebSocketClientError ? error.diagnostics : undefined;
      this.disconnect();
      const msg = error instanceof Error ? error.message : "Connection failed";
      this.onError(msg, diagnostics);
      throw error;
    }
  }

  public send(msg: WsClientMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  public disconnect() {
    this.intentionalClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.connectionState !== ConnectionState.DISCONNECTED) {
      this.updateState(ConnectionState.DISCONNECTED);
    }
  }

  private updateState(state: ConnectionState) {
    this.connectionState = state;
    this.onStateChange(state);
  }

  private handleOnOpen() {
    this.intentionalClose = false;
    this.updateState(ConnectionState.CONNECTED);
    this.onSetupComplete();
  }

  private handleServerError(
    errorMsg: { message?: string } | string,
    details?: WebSocketFailureDetails,
  ) {
    const msg =
      typeof errorMsg === "string"
        ? errorMsg
        : errorMsg.message || JSON.stringify(errorMsg);
    this.onError(msg, details);
    this.updateState(ConnectionState.ERROR);
  }

  private handleOnError(details: WebSocketFailureDetails) {
    console.info("WebSocket diagnostics:", details);
    this.onError("WebSocket connection error", details);
    this.updateState(ConnectionState.ERROR);
  }

  private handleOnClose(
    event: CloseEvent,
    details: WebSocketFailureDetails,
  ) {
    console.info("WebSocket closed:", details);

    const shouldReportUnexpectedClose =
      !this.intentionalClose &&
      this.connectionState !== ConnectionState.CONNECTING &&
      this.connectionState !== ConnectionState.ERROR &&
      event.code !== 1000;

    if (shouldReportUnexpectedClose) {
      this.onError(`WebSocket closed: ${event.code}`, details);
    }

    this.updateState(ConnectionState.DISCONNECTED);
    this.intentionalClose = false;
  }
}
